/**
 * Front Desk - typed BFF client. Mirrors packages/bff/src/desk (deskStore.ts
 * and deskForm.ts; the BFF cannot import @shared, so the types are copied
 * here and must be kept in step).
 *
 * Envelope convention: reads return a named-key object this layer unwraps;
 * errors are `{ error, details? }` and are thrown as DeskApiError.
 */

import { bffFetch } from '@shared/api/bffFetch';

export type StatusCategory =
  | 'new'
  | 'queued'
  | 'active'
  | 'waiting_internal'
  | 'waiting_external'
  | 'verify'
  | 'done'
  | 'cancelled';

export type FieldKind =
  | 'short_text'
  | 'long_text'
  | 'number'
  | 'money'
  | 'date'
  | 'single_select'
  | 'multi_select'
  | 'yes_no'
  | 'person'
  | 'entity'
  | 'heading'
  | 'help';

export interface DeskField {
  key: string;
  kind: FieldKind;
  label: string;
  help?: string;
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  showWhen?: { field: string; equals: string | boolean | string[] };
  agentOnly?: boolean;
  entityKind?: 'group' | 'broker' | 'solution' | 'vera_page';
}

export interface PersonRef {
  id: string;
  name: string;
  email?: string;
}

export type FieldValue = string | number | boolean | string[] | PersonRef | null;

export type ChangelogMode = 'own' | 'fold' | 'fix' | 'hidden';
export type TeamRole = 'agent' | 'collaborator' | 'manager';

export interface RequestTypeSummary {
  id: number;
  key: string;
  name: string;
  description: string;
  portalGroup: string;
  level: number;
  behavior: 'request' | 'idea';
}

export interface TeamSummary {
  id: number;
  key: string;
  name: string;
  description: string;
  requestTypes: RequestTypeSummary[];
}

export interface RequestTypeForm extends RequestTypeSummary {
  team: { id: number; key: string; name: string };
  formVersionId: number;
  fields: DeskField[];
  canTriage: boolean;
}

export interface StatusOption {
  id: number;
  key: string;
  name: string;
  category: StatusCategory;
  sort: number;
  isActive: boolean;
  requesterLabel: string | null;
  color: string;
}

export interface QueueRow {
  id: number;
  key: string;
  name: string;
  view: 'list' | 'board';
  filter: Record<string, unknown>;
}

export interface TeamConfig {
  id: number;
  key: string;
  name: string;
  myRole: TeamRole | null;
  canManage: boolean;
  requestTypes: Array<RequestTypeSummary & { workflowId: number }>;
  workflows: Array<{ id: number; key: string; name: string; statuses: StatusOption[] }>;
  queues: QueueRow[];
  members: Array<PersonRef & { role: TeamRole }>;
}

export interface DeskMe {
  me: { id: string; name: string; isAdmin: boolean };
  teams: Array<{ id: number; key: string; name: string; role: TeamRole | null }>;
  ticketPrefix: string;
}

export interface TicketSummary {
  number: number;
  key: string;
  legacyRef: string | null;
  title: string;
  team: { id: number; name: string };
  requestType: { id: number; key: string; name: string; level: number };
  status: { id: number; key: string; name: string; category: StatusCategory; color: string };
  priority: number | null;
  tags: string[];
  parent: { number: number; title: string } | null;
  childCount: number;
  requester: PersonRef;
  assignee: PersonRef | null;
  dueDate: string | null;
  changelogMode: ChangelogMode;
  rank: number | null;
  statusChangedAt: string;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TimelineComment {
  type: 'comment';
  id: number;
  kind: 'public' | 'internal';
  author: PersonRef;
  body: string;
  createdAt: string;
  editedAt: string | null;
}

export interface TimelineEvent {
  type: 'event';
  id: number;
  kind: string;
  actorName: string;
  payload: Record<string, unknown>;
  createdAt: string;
}

export type LinkKind = 'related' | 'duplicate_of' | 'follow_up_of' | 'insight_for';

export interface TicketLink {
  kind: LinkKind;
  direction: 'out' | 'in';
  number: number;
  title: string;
  statusName: string;
  category: StatusCategory;
}

export interface TicketDetail extends TicketSummary {
  description: string;
  fields: DeskField[];
  fieldValues: Record<string, FieldValue>;
  createdBy: PersonRef;
  participants: PersonRef[];
  timeline: Array<TimelineComment | TimelineEvent>;
  children: TicketSummary[];
  progress: { total: number; done: number; cancelled: number; open: number } | null;
  links: TicketLink[];
  statuses?: StatusOption[];
  viewer: { canWork: boolean; seesAll: boolean; canManage: boolean; isRequester: boolean; isParticipant: boolean };
}

export interface TicketPatch {
  statusId?: number;
  priority?: number | null;
  assignee?: PersonRef | null;
  dueDate?: string | null;
  title?: string;
  description?: string;
  fieldValues?: Record<string, unknown>;
  parentNumber?: number | null;
  tags?: string[];
  changelogMode?: ChangelogMode;
  rank?: number | null;
}

export type TicketScope = 'mine' | 'assigned' | 'team';

export class DeskApiError extends Error {
  constructor(message: string, readonly status: number, readonly details?: { fieldErrors?: Record<string, string> }) {
    super(message);
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await bffFetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new DeskApiError(String(body.error ?? `Request failed (${res.status})`), res.status, body.details as DeskApiError['details']);
  }
  return body as T;
}

const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });

export const fetchMe = () => call<DeskMe>('/api/desk/me');

export const fetchCatalog = () => call<{ teams: TeamSummary[] }>('/api/desk/catalog').then((b) => b.teams);

export const fetchTeam = (id: number) => call<{ team: TeamConfig }>(`/api/desk/teams/${id}`).then((b) => b.team);

export const fetchRequestType = (id: number) =>
  call<{ requestType: RequestTypeForm }>(`/api/desk/request-types/${id}`).then((b) => b.requestType);

export function fetchTickets(opts: { scope: TicketScope; teamId?: number; queueId?: number; parent?: number; open?: boolean }) {
  const qs = new URLSearchParams({ scope: opts.scope });
  if (opts.teamId != null) qs.set('teamId', String(opts.teamId));
  if (opts.queueId != null) qs.set('queueId', String(opts.queueId));
  if (opts.parent != null) qs.set('parent', String(opts.parent));
  if (opts.open != null) qs.set('open', String(opts.open));
  return call<{ tickets: TicketSummary[] }>(`/api/desk/tickets?${qs}`).then((b) => b.tickets);
}

export const fetchTicket = (number: number) =>
  call<{ ticket: TicketDetail }>(`/api/desk/tickets/${number}`).then((b) => b.ticket);

export interface CreateTicketInput {
  requestTypeId: number;
  title: string;
  description: string;
  fieldValues: Record<string, unknown>;
  requester?: PersonRef;
  participants?: PersonRef[];
  priority?: number | null;
  assignee?: PersonRef | null;
  parentNumber?: number | null;
  tags?: string[];
}

export const createTicket = (input: CreateTicketInput) =>
  call<{ ticket: TicketSummary }>('/api/desk/tickets', json('POST', input)).then((b) => b.ticket);

export const updateTicket = (number: number, patch: TicketPatch) =>
  call<{ ticket: TicketDetail }>(`/api/desk/tickets/${number}`, json('PATCH', patch)).then((b) => b.ticket);

export const addComment = (number: number, input: { kind: 'public' | 'internal'; body: string }) =>
  call<{ comment: TimelineComment }>(`/api/desk/tickets/${number}/comments`, json('POST', input)).then((b) => b.comment);

export const answerResolution = (number: number, input: { solved: boolean; comment?: string }) =>
  call<{ ticket: TicketDetail }>(`/api/desk/tickets/${number}/resolution`, json('POST', input)).then((b) => b.ticket);

export const setParticipant = (number: number, input: { person?: PersonRef; present: boolean }) =>
  call<{ ok: boolean }>(`/api/desk/tickets/${number}/participants`, json('PUT', input));

export const setLink = (number: number, input: { kind: LinkKind; otherNumber: number; present: boolean }) =>
  call<{ ok: boolean }>(`/api/desk/tickets/${number}/links`, json('PUT', input));
